import { apiClient } from '@/api/client'
import type { OfflineOrder } from './offlineStore'

/**
 * The smallest difference in tenant currency worth calling a mismatch.
 *
 * Half a paisa. The server and the client both round to 2dp through the same
 * rules, so a genuine agreement is exact; this only absorbs float representation
 * noise (0.1 + 0.2), never a real price difference.
 */
export const AMOUNT_EPSILON = 0.005

/**
 * Settle an order that was PAID during the outage (F-CTRL-N55).
 *
 * Returns true when a manager needs to look at it.
 *
 * THE RULE, AND WHY IT IS THIS WAY
 *
 * The order settles at the SERVER's total, never at the amount collected. The
 * cashier charged what this terminal computed from a menu snapshot that may be
 * hours old; the server reprices from the live menu. If a price or a tax rate
 * moved during the outage, the offline figure is simply wrong, and honouring it
 * would make the offline terminal the pricing authority — which is exactly what
 * invariant #11 forbids and what audit/KARO_TAX_RATE_INCIDENT_2026-08-03.md is
 * a record of.
 *
 * That leaves a real gap: the drawer holds what was collected, the books record
 * what was billed. It is NOT absorbed. It goes to a manager with both figures
 * on it, and Day Close blocks until they bill or write it off (invariant #12).
 * "The till is 60 short, here is why" is a decision; an unexplained variance at
 * close is not.
 *
 * Payment goes through the ORDINARY payment endpoint — the same one the online
 * counter uses. Not a shortcut: that handler owns the tender-intent lock, the
 * tax rate for the tender, the cash-drawer movement (including the cross-day
 * paid_in of F-CTRL-N4) and the fiscal enqueue. Reimplementing any of it for the
 * offline path would be a second copy of money-critical logic, free to drift.
 */
export async function settleOfflinePayment(
  entry: OfflineOrder,
  orderId: string | undefined,
  serverTotal: number | undefined,
): Promise<boolean> {
  const payment = entry.payment
  if (!payment || !orderId) return false

  const collected = payment.amount
  // Fall back to the collected amount only if the server did not report one —
  // an older backend. Settling at 0 would leave the order unpaid.
  const settleAt = typeof serverTotal === 'number' ? serverTotal : collected

  try {
    const res = await apiClient.processPayment(orderId, {
      payment_method: payment.method,
      amount: settleAt,
      // Which online provider took it (F-CTRL-N55). Optional server-side, but
      // without it an offline online-sale lands in the reports' online line
      // with no provider breakdown, unlike every sale taken while connected.
      ...(payment.method === 'online' && payment.subMethod
        ? { payment_sub_method: payment.subMethod }
        : {}),
    })
    if (!res.success) {
      await reportPaymentProblem(entry, 'offline_payment_failed', collected, null)
      return true
    }
  } catch {
    // The order EXISTS and is unpaid, with the cash already in the drawer. That
    // is precisely the "money taken, nothing recorded" case the reconciliation
    // queue exists for, so it must never be swallowed — but it also must not
    // fail the replay, because the order itself is fine.
    await reportPaymentProblem(entry, 'offline_payment_failed', collected, null)
    return true
  }

  if (Math.abs(settleAt - collected) > AMOUNT_EPSILON) {
    await reportPaymentProblem(entry, 'offline_payment_amount_mismatch', collected, settleAt)
    return true
  }
  return false
}

/** Put a payment problem in front of a manager, with both figures. */
async function reportPaymentProblem(
  entry: OfflineOrder,
  failureCode: string,
  collected: number,
  settled: number | null,
): Promise<void> {
  try {
    await apiClient.reportUnreconciledOffline({
      op_id: `${entry.opId}:payment`,
      order_number: entry.ticketRef || entry.orderNumber || '',
      target_order_id: null,
      captured_at: entry.capturedAt,
      failure_code: failureCode,
      lines: entry.lines,
      collected_amount: collected,
      settled_amount: settled,
    })
  } catch {
    // Nothing left to do here. The op id is suffixed so this row is distinct
    // from the order's own entry, and the report is idempotent on op_id, so the
    // next drain re-reports it. Never rethrow: the ORDER synced, and failing the
    // replay over the report would queue the food again.
  }
}

